/**
 * nts-hl-cache.js — v3
 * CartManager — localStorage cache for GQL Storefront API cart state.
 * Pure state management. No API calls. No rendering.
 *
 * Subscribes to: cart:api:success, cart:synced, cart:server:full
 * Emits:         cart:updated, cart:cleared
 */
class CartManager {
  constructor({ bus, storageKey }) {
    this._bus = bus;
    this.storageKey = storageKey || 'nts_hl_cart';

    // Optimistic items never survive a page load — server truth replaces them
    // on success, and on failure they'd linger as undeletable ghosts (fake
    // lineIds → cartLinesRemove returns null → spinner hangs). Prune at boot.
    this.pruneOptimistic(false);

    // Failed API call → any in-flight optimistic ghosts are now lies; drop them.
    bus.on('cart:api:error', () => this.pruneOptimistic(true));

    // Cross-tab sync
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this._bus.emit('cart:updated', { cart: this.getCart(), source: 'tab-sync' });
      }
    });

    // After GQL cartCreate/cartLinesAdd — server response is truth
    bus.on('cart:api:success', ({ cart: gqlCart, action }) => {
      if (gqlCart) {
        this._writeFromGql(gqlCart);
        this._bus.emit('cart:updated', { cart: this.getCart(), source: action || 'api' });
      }
    });

    // After GQL cartQuery verify
    bus.on('cart:synced', ({ cart: gqlCart }) => {
      if (gqlCart) {
        this._writeFromGql(gqlCart);
        this._bus.emit('cart:updated', { cart: this.getCart(), source: 'sync' });
      }
    });

    // After remove/update (full cart response)
    bus.on('cart:server:full', ({ cart: gqlCart, source }) => {
      if (gqlCart) {
        this._writeFromGql(gqlCart);
        this._bus.emit('cart:updated', { cart: this.getCart(), source });
      }
    });
  }

  /* -- Read -- */

  getCart() { return this._read(); }

  hasItem(variantId) {
    return this._read().items.some(i => i.variantId === variantId);
  }

  itemCount() {
    return this._read().totalQuantity || this._read().items.reduce((s, i) => s + i.quantity, 0);
  }

  getSubtotal() {
    return this._read().items.reduce((s, i) => s + (i.price * i.quantity), 0);
  }

  findByAttribute(key, value) {
    return this._read().items.filter(i =>
      (i.attributes || []).some(a => a.key === key && (value === undefined || a.value === value))
    );
  }

  /* -- Write -- */

  clear() {
    this._writeRaw({ items: [], totalQuantity: 0, checkoutUrl: null, shopifyCartId: null });
    this._bus.emit('cart:cleared', {});
    this._bus.emit('cart:updated', { cart: this.getCart(), source: 'clear' });
  }

  /**
   * Optimistic write — append items to the cache immediately, before the GQL
   * round-trip returns. The drawer renders these so the user sees their item
   * the instant they click ATC. When cart:api:success fires, _writeFromGql
   * overwrites the cart with the server's truth (real lineIds, exact prices).
   *
   * Items use temp lineIds prefixed `optimistic-`; drawer.js relies on the
   * `optimistic: true` flag to skip the slide-out animation when these are
   * replaced.
   */
  optimisticAdd(items) {
    if (!items || !items.length) return;
    const cart = this._read();
    const tempId = () => 'optimistic-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    // Merge key = variant + attribute signature, mirroring how Shopify's
    // cartLinesAdd merges lines server-side. Re-adding the same product must
    // bump the existing line's quantity, NOT append a second line that the
    // server then collapses (which flashes a duplicate for a split second).
    const sig = (vid, attrs) =>
      Number(vid) + '|' + JSON.stringify((attrs || []).map(a => [a.key, a.value]).sort());
    const nextItems = (cart.items || []).map(i => ({ ...i }));
    items.forEach(i => {
      const isig = sig(i.variantId, i.attributes);
      const existing = nextItems.find(n => sig(n.variantId, n.attributes) === isig);
      if (existing) {
        existing.quantity = (Number(existing.quantity) || 0) + (Number(i.quantity) || 1);
        existing.lineTotal = null;   // recompute from price×qty until server truth lands
      } else {
        nextItems.push({
          lineId:       tempId(),
          variantId:    Number(i.variantId),
          productId:    i.productId || null,
          title:        i.title || '',
          variantTitle: i.variantTitle || null,
          handle:       i.handle || '',
          price:        Number(i.price) || 0,
          comparePrice: i.comparePrice != null ? Number(i.comparePrice) : null,
          currency:     cart.currency || 'USD',
          quantity:     Number(i.quantity) || 1,
          lineTotal:    null,
          image:        i.image || null,
          attributes:   i.attributes || [],
          optimistic:   true,
        });
      }
    });
    this._writeRaw({
      ...cart,
      items:         nextItems,
      totalQuantity: nextItems.reduce((s, i) => s + i.quantity, 0),
      subtotal:      nextItems.reduce((s, i) => s + (i.price * i.quantity), 0),
    });
    this._bus.emit('cart:updated', { cart: this.getCart(), source: 'optimistic' });
  }

  /**
   * Optimistic removal — drop one or more line IDs from the cache and re-render
   * immediately, before the server round-trip. _writeFromGql reconciles to
   * server truth when the mutation returns.
   */
  optimisticRemove(lineIds) {
    const ids = Array.isArray(lineIds) ? lineIds : [lineIds];
    const cart = this._read();
    const items = (cart.items || []).filter(i => ids.indexOf(i.lineId) === -1);
    this._writeRaw({
      ...cart,
      items,
      totalQuantity: items.reduce((s, i) => s + i.quantity, 0),
      subtotal:      items.reduce((s, i) => s + (i.price * i.quantity), 0),
    });
    this._bus.emit('cart:updated', { cart: this.getCart(), source: 'optimistic-remove' });
  }

  /** Optimistic quantity change for one line. */
  optimisticQty(lineId, quantity) {
    const cart = this._read();
    const items = (cart.items || []).map(i =>
      i.lineId === lineId
        ? { ...i, quantity, lineTotal: (i.price || 0) * quantity }
        : i
    );
    this._writeRaw({
      ...cart,
      items,
      totalQuantity: items.reduce((s, i) => s + i.quantity, 0),
      subtotal:      items.reduce((s, i) => s + (i.price * i.quantity), 0),
    });
    this._bus.emit('cart:updated', { cart: this.getCart(), source: 'optimistic-qty' });
  }

  /** Optimistic discount-code set (array of code strings). */
  optimisticDiscounts(codes) {
    const cart = this._read();
    this._writeRaw({
      ...cart,
      discountCodes: (codes || []).map(c => ({ code: c, applicable: true })),
    });
    this._bus.emit('cart:updated', { cart: this.getCart(), source: 'optimistic-discount' });
  }

  /**
   * Drop optimistic ghost items from the cache. emit=true notifies the drawer
   * (used on api:error); emit=false is the silent boot-time prune.
   */
  pruneOptimistic(emit) {
    const cart = this._read();
    const items = (cart.items || []).filter(i => !i.optimistic);
    if (items.length === (cart.items || []).length) return;
    this._writeRaw({ ...cart, items, totalQuantity: items.reduce((s, i) => s + i.quantity, 0) });
    if (emit) this._bus.emit('cart:updated', { cart: this.getCart(), source: 'prune' });
  }

  /* -- Internal -- */

  _read() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || { items: [] };
    } catch {
      return { items: [] };
    }
  }

  _writeRaw(cart) {
    cart.updatedAt = Date.now();
    try { localStorage.setItem(this.storageKey, JSON.stringify(cart)); }
    catch (e) { this._bus.emit('cart:error', { error: e.message }); }
  }

  /**
   * Write from a GQL Cart object (cartCreate/cartLinesAdd/cartQuery response).
   * GQL response is the single source of truth.
   */
  _writeFromGql(gqlCart) {
    const items = gqlCart.lines.nodes.map(line => {
      const m = line.merchandise;
      return {
        lineId:       line.id,
        variantId:    parseInt(m.id.split('/').pop(), 10),
        productId:    m.product ? m.product.id : null,
        title:        m.product ? m.product.title : '',
        variantTitle: m.title !== 'Default Title' ? m.title : null,
        handle:       m.product ? m.product.handle : '',
        productType:  m.product ? (m.product.productType || '') : '',
        price:        Math.round(parseFloat(m.price.amount) * 100),
        comparePrice: m.compareAtPrice ? Math.round(parseFloat(m.compareAtPrice.amount) * 100) : null,
        currency:     m.price.currencyCode,
        quantity:     line.quantity,
        lineTotal:    line.cost?.totalAmount ? Math.round(parseFloat(line.cost.totalAmount.amount) * 100) : null,
        image:        m.image?.url || m.product?.featuredImage?.url || null,
        attributes:   line.attributes || [],
      };
    });

    const checkoutUrl = gqlCart.checkoutUrl ? gqlCart.checkoutUrl + '&ref=travel&aff=travel' : null;

    this._writeRaw({
      items,
      shopifyCartId:  gqlCart.id,
      checkoutUrl,
      totalQuantity:  gqlCart.totalQuantity,
      subtotal:       gqlCart.cost?.subtotalAmount ? Math.round(parseFloat(gqlCart.cost.subtotalAmount.amount) * 100) : null,
      total:          gqlCart.cost?.totalAmount ? Math.round(parseFloat(gqlCart.cost.totalAmount.amount) * 100) : null,
      currency:       gqlCart.cost?.subtotalAmount?.currencyCode || 'USD',
      discountCodes:  gqlCart.discountCodes || [],
    });
  }
}

window.NTS = window.NTS || {};
